import { useCallback, useEffect, useRef, useState } from 'react'
import { Activity, fetchSiteActivities } from '../../../../services'

const DEFAULT_PAGE_SIZE = 15

export function useSiteActivities(accountId: number) {
  const [activities, setActivities] = useState<Activity[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const pageRef = useRef(0)
  const loadingRef = useRef(false)
  const hasMoreRef = useRef(true)

  const fetchPage = useCallback(
    async (reset: boolean) => {
      if (
        !accountId ||
        loadingRef.current ||
        (!reset && !hasMoreRef.current)
      ) {
        return
      }

      const page = reset ? 0 : pageRef.current
      loadingRef.current = true
      setLoading(true)
      setError(null)

      try {
        const body = await fetchSiteActivities({
          accountId,
          page,
          pageSize: DEFAULT_PAGE_SIZE,
        })
        if (!body?.success) {
          setError(body?.message || 'Failed to load activities')
          hasMoreRef.current = false
          setHasMore(false)
          return
        }

        const items = body.data || []
        setActivities((prev) => {
          const next = reset ? items : [...prev, ...items]
          pageRef.current = Math.floor(next.length / DEFAULT_PAGE_SIZE)
          return next
        })

        if (items.length < DEFAULT_PAGE_SIZE) {
          hasMoreRef.current = false
          setHasMore(false)
        }
      } catch {
        setError('Failed to load activities')
        hasMoreRef.current = false
        setHasMore(false)
      } finally {
        loadingRef.current = false
        setLoading(false)
      }
    },
    [accountId]
  )

  const loadMore = useCallback(() => {
    fetchPage(false)
  }, [fetchPage])

  useEffect(() => {
    pageRef.current = 0
    hasMoreRef.current = true
    loadingRef.current = false
    setHasMore(true)
    setActivities([])
    setError(null)
    fetchPage(true)
  }, [accountId, fetchPage])

  return {
    activities,
    loading,
    error,
    hasMore,
    loadMore,
  }
}
